import {
  createContext,
  createElement,
  Fragment,
  type ReactElement,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
} from 'react';

import type {DualModeReactRenderer} from './DualModeReactRenderer';
import {RampIdType} from './generated/graphql';
import {useUserRampFlagWithLoading} from './util/useRampFlag';

const RenderModeContext = createContext<DualModeReactRenderer | null>(null);

export const RenderModeContextProvider = RenderModeContext.Provider;

function useSetRenderMode() {
  const renderEngine = useContext(RenderModeContext);
  if (!renderEngine) {
    throw new Error(
      'useRenderEngine must be used within a DualModeReactRenderer'
    );
  }

  return useCallback(
    (renderMode: 'concurrent' | 'legacy') => {
      renderEngine.changeRenderMode(renderMode);
    },
    [renderEngine]
  );
}

type AutoRenderModeSwitcherProps = {children: ReactNode};

/**
 * Concurrent rendering is the default (see DualModeReactRenderer). This
 * component treats the `react_concurrent_mode_rollback` gate as a kill switch:
 *
 * - gate resolves false / unset (the normal state) -> stay concurrent
 * - gate resolves true (enabled to roll back a regression)
 *   -> switch the app to the legacy renderer (a one-time unmount/remount)
 *
 * Because the default (and un-ramped) value is false, concurrent is the
 * effective default for everyone — including dedicated customers with no gate
 * configuration — without needing to ramp anything. Enabling the gate is the
 * rollback lever. We wait for the gate to finish loading before acting so we
 * don't needlessly churn the render mode during the initial load.
 *
 * @param children
 */
export function ConcurrentRenderModeSwitcher({
  children,
}: AutoRenderModeSwitcherProps): ReactElement {
  const setRenderMode = useSetRenderMode();
  const {isEnabled: shouldRollbackToLegacy, loading} =
    useUserRampFlagWithLoading(
      'react_concurrent_mode_rollback',
      RampIdType.UserName
    );

  useEffect(() => {
    // Concurrent is the default; only act once the gate has resolved.
    if (loading) {
      return;
    }
    setRenderMode(shouldRollbackToLegacy ? 'legacy' : 'concurrent');
  }, [shouldRollbackToLegacy, loading, setRenderMode]);

  return createElement(Fragment, {}, children);
}
